import { DEPRECATED_backwardsCompatibleValues } from "@faire/web--source/common/localization/localize";
import { EmbeddedLink } from "@faire/web--source/ui/EmbeddedLink";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { trackFaireDirectCreditHcArticleFdLandingPageBottomView } from "@faire/web-api--source/events/faireDirect/view/creditHcArticleFdLandingPageBottom";
import helpCenterArticles from "@faire/web-api--source/routes/support/articles/articleId";
import React, { type JSX } from "react";

export interface IPerkValue {
  head: React.ReactNode;
  body: React.ReactNode;
}

interface IOptions {
  isFaireDirectCreditPercentageBased: boolean;
  formattedIncentiveAmount: string;
  formattedPercentageBasedDiscount: string;
  formattedMaximumPercentageDiscount: string;
  isFoiForFDDiscount: boolean;
}

export const getPerkValues = (options: IOptions): IPerkValue[] => [
  options.isFoiForFDDiscount
    ? getFoiForFDDiscountPerk(options)
    : getDiscountPerk(options),

  {
    head: (
      <StrictLocalMsg
        defaultMessage="60-day payment terms on all orders"
        description={{
          text: "This is for the Why shop us through Faire? section on the Faire Direct sign up page. If the retailer signup through this page they can get this benefit",
          img: "https://cdn.faire.com/fastly/32b4354d622c0a0e2804c889ed198e1f5abc70198f47634b69438651bd9b06ad.png",
        }}
      />
    ),
    body: (
      <StrictLocalMsg defaultMessage="When you order through Faire, use terms to buy now & pay 60 days later." />
    ),
  },
  {
    head: (
      <StrictLocalMsg
        // eslint-disable-next-line @faire/no-mid-sentence-linebreaks
        defaultMessage="Free returns{lineBreak}on opening orders"
        values={DEPRECATED_backwardsCompatibleValues({ lineBreak: <br /> })}
      />
    ),
    body: (
      <StrictLocalMsg defaultMessage="If something's not right, get a prepaid shipping label and send it back." />
    ),
  },
];

const getDiscountPerk = ({
  formattedIncentiveAmount,
  formattedMaximumPercentageDiscount,
  formattedPercentageBasedDiscount,
  isFaireDirectCreditPercentageBased,
}: IOptions): IPerkValue => {
  return {
    head: isFaireDirectCreditPercentageBased ? (
      <StrictLocalMsg
        defaultMessage="{faireDirectPercentageDiscountNumber} off and 1 year of free shipping"
        description={{
          text: "This is for the Why shop us through Faire? section on the page. If the retailer signup through this page they can get this promotion",
          img: "https://cdn.faire.com/fastly/32b4354d622c0a0e2804c889ed198e1f5abc70198f47634b69438651bd9b06ad.png",
        }}
        values={{
          faireDirectPercentageDiscountNumber: formattedPercentageBasedDiscount,
        }}
      />
    ) : (
      <StrictLocalMsg
        defaultMessage="{promoMoney} off & 1 year of free shipping"
        values={{
          promoMoney: formattedIncentiveAmount,
        }}
      />
    ),
    body: isFaireDirectCreditPercentageBased ? (
      <StrictLocalMsg
        defaultMessage="Get up to {maximumDiscountMoney} off your first order with us—covered by Faire."
        description={{
          text: "This is for the Why shop us through Faire? section on the page. If the retailer signup through this page they can get this promotion",
          img: "https://cdn.faire.com/fastly/32b4354d622c0a0e2804c889ed198e1f5abc70198f47634b69438651bd9b06ad.png",
        }}
        values={{
          maximumDiscountMoney: formattedMaximumPercentageDiscount,
        }}
      />
    ) : (
      <StrictLocalMsg
        defaultMessage="Faire is offering this promotion to new retailers to grow its global community of retailers and brands. <LinkFormatting>Learn More</LinkFormatting>"
        values={{
          LinkFormatting: (chunks: JSX.Element) => (
            <EmbeddedLink
              linkTo={helpCenterArticles("360034300092")}
              onClick={() => {
                trackFaireDirectCreditHcArticleFdLandingPageBottomView();
              }}
              target="_blank"
            >
              {chunks}
            </EmbeddedLink>
          ),
        }}
      />
    ),
  };
};

const getFoiForFDDiscountPerk = ({
  formattedIncentiveAmount,
  formattedMaximumPercentageDiscount,
  formattedPercentageBasedDiscount,
  isFaireDirectCreditPercentageBased,
}: IOptions): IPerkValue => {
  return {
    head: isFaireDirectCreditPercentageBased ? (
      <StrictLocalMsg
        defaultMessage="{faireDirectPercentageDiscountNumber} off and 1 year of free shipping"
        description={{
          text: "This is for the Why shop us through Faire? section on the page. If the retailer signup through this page they can get this promotion",
          img: "https://cdn.faire.com/fastly/32b4354d622c0a0e2804c889ed198e1f5abc70198f47634b69438651bd9b06ad.png",
        }}
        values={{
          faireDirectPercentageDiscountNumber: formattedPercentageBasedDiscount,
        }}
      />
    ) : (
      <StrictLocalMsg
        defaultMessage="{promoMoney} off & 1 year of free shipping"
        values={{
          promoMoney: formattedIncentiveAmount,
        }}
      />
    ),
    body: isFaireDirectCreditPercentageBased ? (
      <StrictLocalMsg
        defaultMessage="Claim our referral offer to get up to {maximumDiscountMoney} off your first order with us and 1 year of free shipping."
        description={{
          text: "This is for the Why shop us through Faire section on the page. If the retailer signup through this page they can get this promotion",
          img: "https://cdn.faire.com/fastly/e3f6a42756f711287659571c002e29353df0b50a2ecf0f6205ae012866dac165.png",
        }}
        values={{
          maximumDiscountMoney: formattedMaximumPercentageDiscount,
        }}
      />
    ) : (
      <StrictLocalMsg
        defaultMessage="Claim our referral offer to get {promoMoney} off your first order with us and 1 year of free shipping."
        description={{
          text: "This is for the Why shop us through Faire section on the page. If the retailer signup through this page they can get this promotion",
          img: "https://cdn.faire.com/fastly/b5e78d9c0e3b87aa3a8531bea9cfd7042ec87ced819b060fce72f62800d14592.png",
        }}
        values={{
          promoMoney: formattedIncentiveAmount,
          LinkFormatting: (chunks: JSX.Element) => (
            <EmbeddedLink
              linkTo={helpCenterArticles("360034300092")}
              onClick={() => {
                trackFaireDirectCreditHcArticleFdLandingPageBottomView();
              }}
              target="_blank"
            >
              {chunks}
            </EmbeddedLink>
          ),
        }}
      />
    ),
  };
};
